import { useState, useCallback, useLayoutEffect, useMemo, ReactNode } from 'react';
import { useRouter, usePathname } from '@/hooks/use-routing';
import { useHotkeys, SHORTCUTS } from '@sharpe/ui/keyboard';
import { useInvalidateWorkspaces } from '@/components/use-workspaces';
import { SidebarToggleContext, SidebarDataContext } from './use-sidebar';

interface SidebarProviderProps {
  children: ReactNode;
  defaultOpen?: boolean;
}

const SIDEBAR_STORAGE_KEY = 'sidebar-open';

function getInitialSidebarState(defaultOpen: boolean): boolean {
  try {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (stored !== null) return stored === 'true';
  } catch {
    // localStorage may be unavailable in private browsing
  }
  return defaultOpen;
}

export function SidebarProvider({ children, defaultOpen = true }: SidebarProviderProps) {
  const [isOpen, setIsOpen] = useState(() => getInitialSidebarState(defaultOpen));
  const [activePreviewId, setActivePreviewId] = useState<string | null>(null);
  const router = useRouter();
  const pathname = usePathname();
  const invalidateConversations = useInvalidateWorkspaces();

  const urlWorkspaceId = pathname.match(/^\/workspace\/([^/]+)/)?.[1] ?? null;
  const urlChatId = pathname.match(/^\/chat\/([^/]+)/)?.[1] ?? null;

  const toggleSidebar = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
      } catch {
        // localStorage may be unavailable in private browsing
      }
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--sidebar-w', isOpen ? '256px' : '52px');
  }, [isOpen]);

  useHotkeys(
    {
      [SHORTCUTS.TOGGLE_SIDEBAR.key]: (e) => {
        e.preventDefault();
        toggleSidebar();
      },
      [SHORTCUTS.HOME.key]: (e) => {
        e.preventDefault();
        router.push('/');
      },
    },
    { enabled: true }
  );

  const toggleValue = useMemo(() => ({ isOpen, toggleSidebar }), [isOpen, toggleSidebar]);

  const dataValue = useMemo(
    () => ({
      invalidateConversations,
      activePreviewId,
      setActivePreviewId,
      urlWorkspaceId,
      urlChatId,
    }),
    [invalidateConversations, activePreviewId, urlWorkspaceId, urlChatId]
  );

  return (
    <SidebarToggleContext.Provider value={toggleValue}>
      <SidebarDataContext.Provider value={dataValue}>
        <div className="flex h-screen relative">{children}</div>
      </SidebarDataContext.Provider>
    </SidebarToggleContext.Provider>
  );
}
